import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import {
    Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    LinearProgress, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material'
import { ImportResult, schoolApiSlice } from '../../../features/school/schoolSlice'
import { ui, tone } from '../../../utils/uiTokens'
import { downloadFile } from './downloadFile'

interface Props {
    schoolId: number
    onClose: () => void
}

// Файл -> предпросмотр (ничего не создаётся) -> подтверждение: создаются только строки OK
const ImportStudentsDialog: React.FC<Props> = ({ schoolId, onClose }) => {
    const { formatMessage } = useIntl()
    const [file, setFile] = useState<File | null>(null)
    const [preview, setPreview] = useState<ImportResult | null>(null)
    const [allowDuplicates, setAllowDuplicates] = useState(false)
    const [importStudents, { isLoading }] = schoolApiSlice.useImportSchoolStudentsMutation()

    const runPreview = async (f: File, allow: boolean) => {
        try {
            setPreview(await importStudents({ file: f, schoolId, dryRun: true, allowDuplicates: allow }).unwrap())
        } catch { setPreview(null) }
    }

    const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] ?? null
        e.target.value = ''
        setFile(f)
        setPreview(null)
        if (f) runPreview(f, allowDuplicates)
    }

    const handleAllow = (allow: boolean) => {
        setAllowDuplicates(allow)
        if (file) runPreview(file, allow)
    }

    const handleSave = async () => {
        if (!file) return
        try {
            const result = await importStudents({ file, schoolId, dryRun: false, allowDuplicates }).unwrap()
            toast.success(formatMessage({ id: 'schoolImportDone' }, { count: result.createdCount }))
            onClose()
        } catch { /* общий обработчик */ }
    }

    const okCount = preview?.rows.filter((r) => r.status === 'OK').length ?? 0
    const errorCount = (preview?.rows.length ?? 0) - okCount

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>{formatMessage({ id: 'schoolImport' })}</DialogTitle>
            <DialogContent>
                <Box sx={{ color: ui.sub, fontSize: '13.5px', mb: 2 }}>
                    {formatMessage({ id: 'schoolImportHint' })}{' '}
                    <Button size="small" onClick={() => downloadFile('school/students/template', 'students-template.xlsx')}>
                        {formatMessage({ id: 'schoolTemplate' })}
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button variant="outlined" component="label">
                        {formatMessage({ id: 'schoolChooseFile' })}
                        <input hidden type="file" accept=".xlsx" onChange={handleFile} />
                    </Button>
                    {file && <Box sx={{ color: ui.ink }}>{file.name}</Box>}
                    <FormControlLabel
                        control={<Checkbox checked={allowDuplicates} onChange={(e) => handleAllow(e.target.checked)} />}
                        label={formatMessage({ id: 'schoolAllowDuplicates' })}
                    />
                </Box>

                {isLoading && <LinearProgress sx={{ mt: 2 }} />}

                {preview && (
                    <>
                        <Alert severity={errorCount ? 'warning' : 'success'} sx={{ mt: 2 }}>
                            {formatMessage({ id: 'schoolImportSummary' }, { ok: okCount, errors: errorCount })}
                        </Alert>
                        <Box sx={{ maxHeight: 360, overflow: 'auto', mt: 2 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{formatMessage({ id: 'schoolRow' })}</TableCell>
                                        <TableCell>{formatMessage({ id: 'fullName' })}</TableCell>
                                        <TableCell>{formatMessage({ id: 'grade' })}</TableCell>
                                        <TableCell>{formatMessage({ id: 'schoolStatus' })}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {preview.rows.map((r) => (
                                        <TableRow key={r.rowNumber}>
                                            <TableCell>{r.rowNumber}</TableCell>
                                            <TableCell>{r.lastName} {r.firstName}</TableCell>
                                            <TableCell>{r.grade ? `${r.grade}${r.literal ?? ''}` : '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={formatMessage({ id: `schoolImport_${r.status}` })}
                                                    sx={{
                                                        fontWeight: 600,
                                                        backgroundColor: r.status === 'OK' ? tone.ok.bg : tone.warn.bg,
                                                        color: r.status === 'OK' ? ui.green : tone.warn.text,
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>{formatMessage({ id: 'close' })}</Button>
                <Button variant="contained" disabled={!okCount || isLoading} onClick={handleSave}>
                    {formatMessage({ id: 'schoolImportSave' }, { count: okCount })}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ImportStudentsDialog
